// Extracted from HomePage.jsx to reduce component complexity.
// All bilingual (DE/EN) data arrays used by the home page.

import {
  Award, Clock, Users, Globe, CheckCircle, Shield, Settings, Zap,
  Monitor, Headphones, BookOpen, Layers,
  Building, UserCheck, GraduationCap, Paintbrush
} from 'lucide-react';

// The 4 solution categories shown in the "What We Offer" grid
export const solutionCategories = [
  {
    id: 'unterweisungssystem',
    title: { de: 'Unterweisungssystem', en: 'Instruction System' },
    description: {
      de: 'Verwalten, organisieren und dokumentieren Sie alle Unterweisungen zentral – mit unserem digitalen Unterweisungssystem.',
      en: 'Manage, organize and document all instructions centrally – with our digital instruction system.'
    },
    icon: Building,
    link: '/solutions#unterweisungssystem',
    hasDetailPage: true,
    detailLink: '/instruction-system'
  },
  {
    id: 'besucherunterweisung',
    title: { de: 'Besucherunterweisung', en: 'Visitor Instruction' },
    description: {
      de: 'Digitale Lösungen für die Unterweisung von Besuchern, Fremdfirmen und Lieferanten – einfach, effizient und rechtssicher.',
      en: 'Digital solutions for instructing visitors, contractors and suppliers – simple, efficient and legally compliant.'
    },
    icon: UserCheck,
    link: '/solutions#besucherunterweisung',
    hasDetailPage: true,
    detailLink: '/visitor-system'
  },
  {
    id: 'etraining-inhalt',
    title: { de: 'Standardisierte E-Trainings', en: 'Standardized E-Trainings' },
    description: {
      de: 'Fertige digitale Trainings für Arbeitssicherheit, IT-Awareness, Compliance und betriebliche Weiterbildung – sofort einsatzbereit und rechtssicher.',
      en: 'Ready-made digital training for workplace safety, IT awareness, compliance and corporate training – ready to use and legally compliant.'
    },
    icon: GraduationCap,
    subcategories: {
      de: ['Arbeitssicherheit', 'Compliance', 'Cyber Security Awareness Trainings (Fit4)'],
      en: ['Workplace Safety', 'Compliance', 'Cyber Security Awareness Trainings (Fit4)']
    },
    link: '/produktkatalog',
    hasCatalog: true
  },
  {
    id: 'elearning-produktion',
    title: { de: 'Individuelle E-Learnings', en: 'Custom E-Learnings' },
    description: {
      de: 'Wir entwickeln maßgeschneiderte digitale Trainings – abgestimmt auf Ihr Unternehmen, Ihre Prozesse und Ihre Inhalte.',
      en: 'We develop customized digital training – tailored to your company, your processes and your content.'
    },
    icon: Paintbrush,
    link: '/solutions#elearning-produktion'
  }
];

// Animated hero text sequences (format required by react-type-animation)
export const animatedTextsDe = [
  'digitalisieren Unterweisungen', 2500,
  'entwickeln E-Learnings', 2500,
  'liefern Komplettlösungen', 2500,
  'schaffen Rechtssicherheit', 2500,
  'reduzieren Kosten', 2500
];

export const animatedTextsEn = [
  'digitize instructions', 2500,
  'develop e-learnings', 2500,
  'deliver complete solutions', 2500,
  'ensure legal compliance', 2500,
  'reduce costs', 2500
];

// Stat cards displayed in the hero section (right side)
export const heroStats = [
  { value: '100+', label: { de: 'Lösungen geliefert', en: 'Solutions Delivered' }, icon: Award, link: '/portfolio' },
  { value: '80+',  label: { de: 'Jahre Erfahrung mit E-Learning', en: 'Years of E-Learning Experience' }, icon: Clock, link: '/about' },
  { value: '200+', label: { de: 'Zufriedene Kunden und Partner', en: 'Happy Clients and Partners' }, icon: Users, link: '/partner' },
  { value: '10+',  label: { de: 'Sprachen', en: 'Languages' }, icon: Globe, link: '/produktkatalog' }
];

// "Why e-Matrix?" benefit cards
export const benefits = [
  { key: 'ready',        title: { de: 'Sofort einsatzbereit',       en: 'Ready to Use' },             desc: { de: 'Unsere Standardmodule sind direkt nutzbar und nahtlos integrierbar.', en: 'Our standard modules are instantly available and seamlessly integrable.' }, icon: CheckCircle },
  { key: 'multilingual', title: { de: 'Mehrsprachig',               en: 'Multilingual' },             desc: { de: 'Verfügbar in mehreren Sprachen für internationale Teams.',             en: 'Available in multiple languages for international teams.' },             icon: Globe },
  { key: 'compliant',    title: { de: 'Gesetzeskonform',            en: 'Legally Compliant' },        desc: { de: 'Erfüllt alle rechtlichen Anforderungen und Compliance-Standards.',     en: 'Meets all legal requirements and compliance standards.' },                icon: Shield },
  { key: 'scalable',     title: { de: 'Anpassbar & Skalierbar',     en: 'Customizable & Scalable' },  desc: { de: 'Flexibel an Ihr Corporate Design und Ihre Anforderungen anpassbar.',   en: 'Flexibly adaptable to your corporate design and requirements.' },         icon: Settings },
  { key: 'efficient',    title: { de: 'Zeit- & Kostensparend',      en: 'Time & Cost Saving' },       desc: { de: 'Automatisierte Prozesse reduzieren Verwaltungsaufwand.',              en: 'Automated processes reduce administrative overhead.' },                   icon: Clock },
  { key: 'secure',       title: { de: 'Sicher & Zuverlässig',       en: 'Secure & Reliable' },        desc: { de: 'Höchste Sicherheitsstandards für Ihre Unternehmensdaten.',            en: 'Highest security standards for your company data.' },                     icon: Zap }
];

// ematrixsys platform feature bullets
export const platformFeatures = [
  { key: 'userFriendly', title: { de: 'Benutzerfreundlich', en: 'User-Friendly' }, desc: { de: 'Intuitive Oberfläche für einfachen Zugang zu Inhalten.',      en: 'Intuitive interface for easy access to content.' },       icon: Monitor },
  { key: 'automation',   title: { de: 'Automatisierung',   en: 'Automation' },    desc: { de: 'Automatische Kurszuweisungen, Erinnerungen und Zertifikate.', en: 'Automatic course assignments, reminders, and certificates.' }, icon: Zap },
  { key: 'flexible',     title: { de: 'Flexibel',           en: 'Flexible' },      desc: { de: 'Anpassbar an Ihre spezifischen Anforderungen.',               en: 'Adaptable to your specific requirements.' },              icon: Settings },
  { key: 'secure',       title: { de: 'Sicher',             en: 'Secure' },        desc: { de: 'Enterprise-grade Sicherheit für Ihre Daten.',                 en: 'Enterprise-grade security for your data.' },              icon: Shield }
];

// "All-in-One" service cards (dark section)
export const services = [
  { key: 'integration', title: { de: 'Technische Integration', en: 'Technical Integration' }, desc: { de: 'Verschiedene Schnittstellen zu Ihren bestehenden Systemen.',      en: 'Various interfaces to your existing systems.' },            icon: Layers },
  { key: 'content',     title: { de: 'E-Learning Inhalte',     en: 'E-Learning Content' },    desc: { de: 'Maßgeschneidert oder sofort einsatzbereit – flexibel und skalierbar.', en: 'Custom-designed or ready-to-use — flexible and scalable.' }, icon: BookOpen },
  { key: 'consulting',  title: { de: 'Beratung',               en: 'Consulting' },            desc: { de: 'Wir unterstützen Sie bei der strategischen Planung.',             en: 'We support you with strategic planning.' },                  icon: Users },
  { key: 'support',     title: { de: 'Support',                en: 'Support' },               desc: { de: 'Unser Team steht Ihnen bei Fragen zur Verfügung.',                en: 'Our team is available for your questions.' },                icon: Headphones }
];
